import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { GoogleAuthButton } from "../components/GoogleAuthButton";
import { Seo } from "../components/Seo";
import { toast } from "sonner";
import { motion } from "framer-motion";

const QUOTE_IMG = "/brand/login-portrait.png";
const EASE = [0.22, 1, 0.36, 1];

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get("next") || "/dashboard";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate(nextPath);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not sign in");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen surface-dark">
      <Seo
        title="Sign in | PrestiqueResume"
        description="Sign in to PrestiqueResume to continue building your recruiter-ready CV."
        path="/login"
        noindex
      />
      <Header variant="dark" />

      <div className="grid lg:grid-cols-[1.05fr_1fr] min-h-[calc(100vh-72px)]">
        {/* LEFT — cinematic portrait with dark cinematic gradient + editorial pull-quote */}
        <div className="relative hidden lg:block overflow-hidden bg-matte-black">
          <img
            src={QUOTE_IMG}
            alt="Editorial portrait — recruiter-ready candidate at her desk"
            className="absolute inset-0 w-full h-full object-cover opacity-80"
          />
          {/* Heavy gradients — masks any baked-in text on the asset & pulls the photograph into the cinematic surface */}
          <div className="absolute inset-0 bg-gradient-to-tr from-matte-black via-matte-black/65 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-matte-black via-matte-black/15 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-matte-black/70" />
          {/* Extra bottom-left mask: hides any baked editorial caption sitting in this region of the asset */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(15,17,21,0.95),rgba(15,17,21,0.65)_30%,transparent_55%)]" />
          <div className="absolute inset-0 bg-deep-navy/20 mix-blend-multiply" />
          <div className="grain-dark absolute inset-0" />

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, ease: EASE, delay: 0.2 }}
            className="relative h-full flex flex-col justify-end p-14 xl:p-20"
          >
            <div className="eyebrow-gold mb-6">Member · No.07</div>
            <div className="font-display text-2xl xl:text-3xl leading-[1.25] tracking-[-0.01em] text-warm-ivory max-w-md">
              Pick up where you <span className="editorial-accent">left off</span>.
            </div>
            <div className="font-mono uppercase tracking-[0.22em] text-[10px] mt-5 text-warm-ivory/45">— PrestiqueResume</div>
          </motion.div>
        </div>

        {/* RIGHT — form on cinematic dark */}
        <div className="relative flex items-center justify-center px-6 sm:px-10 py-16 lg:py-0">
          <div className="absolute inset-0 spotlight-glow opacity-60 pointer-events-none" />
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
            data-testid="login-form"
            className="relative w-full max-w-md"
          >
            <div className="eyebrow-gold mb-5">Member · Sign in</div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-[-0.02em] leading-[1.04] mb-10 text-warm-ivory">
              Welcome<br />
              <span className="editorial-accent">back.</span>
            </h1>

            <GoogleAuthButton testid="login-google-btn" />
            <div className="my-7 flex items-center gap-4 text-warm-ivory/35">
              <hr className="flex-1 hairline-dark" />
              <span className="font-mono text-[10px] uppercase tracking-[0.3em]">or sign in with email</span>
              <hr className="flex-1 hairline-dark" />
            </div>

            <div className="space-y-7">
              <div>
                <label className="eyebrow-gold block mb-3">Email</label>
                <input
                  type="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email"
                  autoComplete="email"
                  className="w-full bg-transparent border-0 border-b border-warm-ivory/20 pb-2 text-warm-ivory placeholder-warm-ivory/30 focus:outline-none focus:border-burnished-gold transition-colors text-base"
                />
              </div>
              <div>
                <label className="eyebrow-gold block mb-3">Password</label>
                <input
                  type="password" required value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                  autoComplete="current-password"
                  className="w-full bg-transparent border-0 border-b border-warm-ivory/20 pb-2 text-warm-ivory placeholder-warm-ivory/30 focus:outline-none focus:border-burnished-gold transition-colors text-base"
                />
              </div>
              <Button
                disabled={loading}
                data-testid="login-submit"
                className="btn-gold w-full h-12 rounded-full font-mono uppercase tracking-[0.20em] text-[11px]"
              >
                {loading ? "Signing in…" : "Sign in"}
              </Button>
            </div>

            <div className="mt-8 flex items-center justify-between text-sm">
              <Link
                to="/forgot-password"
                data-testid="forgot-link"
                className="text-warm-ivory/65 hover:text-burnished-gold underline underline-offset-4 decoration-warm-ivory/15 hover:decoration-burnished-gold transition-colors"
              >
                Forgot password?
              </Link>
              <Link
                to={`/signup${nextPath !== "/dashboard" ? `?next=${encodeURIComponent(nextPath)}` : ""}`}
                className="text-burnished-gold hover:text-warm-ivory font-mono uppercase tracking-[0.18em] text-[11px] transition-colors"
              >
                Create account →
              </Link>
            </div>
          </motion.form>
        </div>
      </div>

      <Footer variant="dark" />
    </div>
  );
};

export default Login;
